import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { Button } from './ui/button';
import { Camera, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const FaceCapture = ({ 
  onCapture, 
  onCancel, 
  mode = 'register', // 'register' or 'attendance'
  staffDescriptors = [], // For attendance mode - list of {staff_id, full_name, face_descriptor}
  onStaffIdentified = null // Callback when staff is identified in attendance mode
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedDescriptor, setCapturedDescriptor] = useState(null);
  const [identifiedStaff, setIdentifiedStaff] = useState(null);
  const [matchScore, setMatchScore] = useState(0);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [verificationCountdown, setVerificationCountdown] = useState(0);
  
  // For auto-capture
  const autoCaptureTimeoutRef = useRef(null);
  const verifiedStaffRef = useRef(null);
  const verifiedDescriptorRef = useRef(null);
  const hasAutoCapturedRef = useRef(false);
  const consecutiveMatchCountRef = useRef(0);
  
  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setIsLoading(true);
        const MODEL_URL = '/models';
        
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        
        setIsModelLoaded(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading models:', err);
        setError('Failed to load face detection models');
        setIsLoading(false);
      }
    };
    
    loadModels();
  }, []);
  
  // Start camera
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Unable to access camera. Please grant camera permissions.');
      }
    };
    
    if (isModelLoaded) {
      startCamera();
    }
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isModelLoaded]);
  
  // Face detection loop - optimized without liveness
  const detectFace = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !isModelLoaded || capturedImage) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video.readyState !== 4) {
      requestAnimationFrame(detectFace);
      return;
    }
    
    const displaySize = { width: video.videoWidth, height: video.videoHeight };
    faceapi.matchDimensions(canvas, displaySize);
    
    const detections = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (detections) {
      // Draw face box
      const resizedDetections = faceapi.resizeResults(detections, displaySize);
      faceapi.draw.drawDetections(canvas, resizedDetections);
      
      setFaceDetected(true);
      
      // In attendance mode, try to identify the person
      if (mode === 'attendance' && staffDescriptors.length > 0) {
        const currentDescriptor = Array.from(detections.descriptor);
        let bestMatch = null;
        let bestDistance = 0.5; // Stricter threshold for better accuracy (was 0.6)
        
        for (const staff of staffDescriptors) {
          if (staff.face_descriptor) {
            const distance = faceapi.euclideanDistance(
              currentDescriptor,
              staff.face_descriptor
            );
            if (distance < bestDistance) {
              bestDistance = distance;
              bestMatch = staff;
            }
          }
        }
        
        if (bestMatch) {
          const score = Math.round((1 - bestDistance) * 100);
          setIdentifiedStaff(bestMatch);
          setMatchScore(score);
          
          // Store for auto-capture
          verifiedStaffRef.current = bestMatch;
          verifiedDescriptorRef.current = currentDescriptor;
          
          if (onStaffIdentified) {
            onStaffIdentified(bestMatch, currentDescriptor);
          }
          
          // Increment consecutive match counter for stability
          consecutiveMatchCountRef.current++;
          
          // Auto-capture after 3 consecutive matches (about 0.5 seconds of stable detection)
          if (consecutiveMatchCountRef.current >= 3 && !autoCapturing && !capturedImage && !hasAutoCapturedRef.current) {
            hasAutoCapturedRef.current = true;
            setAutoCapturing(true);
            setVerificationCountdown(2);
            
            // Quick countdown and auto-capture
            let countdown = 2;
            const countdownInterval = setInterval(() => {
              countdown--;
              setVerificationCountdown(countdown);
              if (countdown <= 0) {
                clearInterval(countdownInterval);
                performAutoCapture();
              }
            }, 400);
            
            autoCaptureTimeoutRef.current = countdownInterval;
          }
        } else {
          setIdentifiedStaff(null);
          setMatchScore(0);
          verifiedStaffRef.current = null;
          verifiedDescriptorRef.current = null;
          consecutiveMatchCountRef.current = 0;
          
          // Cancel auto-capture if face lost or not matched
          if (autoCaptureTimeoutRef.current) {
            clearInterval(autoCaptureTimeoutRef.current);
            autoCaptureTimeoutRef.current = null;
            setAutoCapturing(false);
            setVerificationCountdown(0);
            hasAutoCapturedRef.current = false;
          }
        }
      }
      
    } else {
      setFaceDetected(false);
      setIdentifiedStaff(null);
      setMatchScore(0);
      consecutiveMatchCountRef.current = 0;
    }
    
    requestAnimationFrame(detectFace);
  }, [isModelLoaded, capturedImage, mode, staffDescriptors, onStaffIdentified, autoCapturing]);
  
  useEffect(() => {
    if (isModelLoaded && !capturedImage) {
      const animationId = requestAnimationFrame(detectFace);
      return () => cancelAnimationFrame(animationId);
    }
  }, [isModelLoaded, capturedImage, detectFace]);
  
  // Cleanup auto-capture timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCaptureTimeoutRef.current) {
        clearInterval(autoCaptureTimeoutRef.current);
      }
    };
  }, []);
  
  // Auto-capture for attendance mode
  const performAutoCapture = async () => {
    if (!videoRef.current || !verifiedStaffRef.current || capturedImage) return;
    
    const video = videoRef.current;
    
    // Stop the camera stream immediately
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Capture the image
    const captureCanvas = document.createElement('canvas');
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    const ctx = captureCanvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = captureCanvas.toDataURL('image/jpeg', 0.8);
    
    setCapturedImage(imageData);
    setCapturedDescriptor(verifiedDescriptorRef.current);
    
    // Auto-confirm
    onCapture({
      image: imageData,
      descriptor: verifiedDescriptorRef.current,
      identifiedStaff: verifiedStaffRef.current
    });
  };
  
  // Manual capture (for register mode)
  const handleCapture = async () => {
    if (!videoRef.current || !faceDetected) return;
    
    const video = videoRef.current;
    
    const captureCanvas = document.createElement('canvas');
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    const ctx = captureCanvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const detections = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();
    
    if (detections) {
      const imageData = captureCanvas.toDataURL('image/jpeg', 0.8);
      const descriptor = Array.from(detections.descriptor);
      
      setCapturedImage(imageData);
      setCapturedDescriptor(descriptor);
    }
  };
  
  // Confirm capture
  const handleConfirm = () => {
    if (capturedImage && capturedDescriptor) {
      onCapture({
        image: capturedImage,
        descriptor: capturedDescriptor,
        identifiedStaff: identifiedStaff
      });
    }
  };
  
  // Retake photo
  const handleRetake = async () => {
    setCapturedImage(null);
    setCapturedDescriptor(null);
    setIdentifiedStaff(null);
    setMatchScore(0);
    setAutoCapturing(false);
    setVerificationCountdown(0);
    hasAutoCapturedRef.current = false;
    consecutiveMatchCountRef.current = 0;
    
    // Restart camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error restarting camera:', err);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-900 rounded-lg">
        <Loader2 className="h-12 w-12 text-white animate-spin mb-4" />
        <p className="text-white">Loading face detection models...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-red-900 rounded-lg">
        <AlertCircle className="h-12 w-12 text-red-300 mb-4" />
        <p className="text-white text-center">{error}</p>
        <Button onClick={onCancel} variant="outline" className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center bg-gray-900 rounded-lg overflow-hidden">
      {/* Camera View */}
      <div className="relative w-full max-w-md">
        {capturedImage ? (
          <img 
            src={capturedImage} 
            alt="Captured" 
            className="w-full rounded-t-lg"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-t-lg transform scale-x-[-1]"
            />
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full transform scale-x-[-1]"
            />
          </>
        )}
        
        {/* Face detection indicator */}
        {!capturedImage && (
          <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${
            faceDetected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {faceDetected ? 'Face Detected' : 'No Face'}
          </div>
        )}
        
        {/* Identified staff indicator (attendance mode) */}
        {!capturedImage && mode === 'attendance' && identifiedStaff && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium bg-blue-500 text-white">
            {identifiedStaff.full_name} ({matchScore}%)
          </div>
        )}
      </div>
      
      {/* Status Display */}
      {!capturedImage && (
        <div className="w-full px-4 py-3 bg-gray-800">
          <div className="text-center">
            {mode === 'attendance' ? (
              identifiedStaff ? (
                autoCapturing ? (
                  <div className="p-3 bg-green-600 rounded-lg">
                    <p className="text-white font-bold text-lg">{identifiedStaff.full_name}</p>
                    <p className="text-green-200 text-sm">Match: {matchScore}% - Capturing in {verificationCountdown}...</p>
                  </div>
                ) : (
                  <div className="p-3 bg-blue-600 rounded-lg">
                    <p className="text-white font-bold">{identifiedStaff.full_name}</p>
                    <p className="text-blue-200 text-sm">Match: {matchScore}% - Hold steady...</p>
                  </div>
                )
              ) : faceDetected ? (
                <div className="p-3 bg-yellow-600 rounded-lg">
                  <p className="text-white font-medium">Face not recognized</p>
                  <p className="text-yellow-200 text-sm">Please ensure you are registered</p>
                </div>
              ) : (
                <div className="p-3 bg-gray-700 rounded-lg">
                  <p className="text-white">Position your face in the frame</p>
                </div>
              )
            ) : (
              <div className="p-3 bg-gray-700 rounded-lg">
                <p className="text-white">
                  {faceDetected ? 'Face detected - Ready to capture!' : 'Position your face in the frame'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="w-full p-4 bg-gray-800 flex gap-3">
        {capturedImage ? (
          <>
            <Button 
              onClick={handleRetake} 
              variant="outline" 
              className="flex-1 bg-transparent border-white text-white hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retake
            </Button>
            <Button 
              onClick={handleConfirm} 
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={onCancel} 
              variant="outline" 
              className="flex-1 bg-transparent border-white text-white hover:bg-gray-700"
            >
              Cancel
            </Button>
            {mode === 'register' && (
              <Button 
                onClick={handleCapture} 
                disabled={!faceDetected}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                <Camera className="h-4 w-4 mr-2" />
                Capture
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FaceCapture;
